// src/components/ResultStep.js
import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { L_SIZE, DPI } from "../App"; // L判サイズと解像度の定義をインポート

const ResultStep = ({
  layoutImageUrl,
  template,
  onDownload,
  onReset,
  outputFormat,
  isActive,
}) => {
  const { t } = useTranslation();
  const [scale, setScale] = useState(1);
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // 実際のL判サイズをmm単位で取得
  const lWidthMm = L_SIZE.width;
  const lHeightMm = L_SIZE.height;

  // ウィンドウサイズに応じて適切なスケールを計算
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current && imageRef.current) {
        // 画面の物理的なDPI（表示ピクセル密度）を考慮
        // 典型的なモニターの解像度は96DPIだが、デバイスによって異なる
        const screenDpi = 96; // 一般的なモニターのDPI

        // 実際の物理サイズ（mm）をピクセルに変換
        // 25.4mm = 1インチ
        const lWidthPx = (lWidthMm / 25.4) * screenDpi;

        // コンテナの幅と画像の実サイズを比較
        const containerWidth = containerRef.current.clientWidth;

        // コンテナにフィットするスケール（余白を考慮して少し小さめに）
        const fitScale = (containerWidth * 0.95) / lWidthPx;

        // スマホなど画面が小さい場合は縮小、大きい場合は1（実寸）を超えないように
        const newScale = Math.min(1, fitScale);
        setScale(newScale);
      }
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [lWidthMm]);

  // 非アクティブな場合は表示しない
  if (!isActive) return null;

  return (
    <div
      className={`result-container ${isActive ? "active" : ""}`}
      ref={containerRef}
    >
      <h2 className="section-title">{t("step_result")}</h2>

      {layoutImageUrl && (
        <div className="result-images">
          <div className="layout-image">
            <div
              className="actual-size-container"
              style={{
                // L判実寸表示用のスタイル
                width: `${lWidthMm}mm`,
                height: `${lHeightMm}mm`,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                margin: scale < 1 ? `0 0 ${(1 - scale) * lHeightMm}mm 0` : "0",
              }}
            >
              <img
                ref={imageRef}
                src={layoutImageUrl}
                alt={t("result.layout_alt", "L size layout")}
                className="result-img"
                style={{
                  width: "100%",
                  height: "100%",
                }}
              />
            </div>
            <p className="actual-size-info">
              {t("result.actual_size_info", "↑ Displayed in actual L size (89mm x 127mm)")}
            </p>
            <p className="layout-info">
              {t("result.layout_text", {
                name: t(template.translationKey),
                description: template.description
              }) || `4 photos of ${t(template.translationKey)} (${template.description}) are placed on L size.`}
            </p>
          </div>
        </div>
      )}

      <div className="print-instructions">
        <h3>{t("result.print_title", "How to print:")}</h3>
        <ol>
          <li>{t("result.print_step1", "Click the 'Download Image' button below to save the image")}</li>
          <li>{t("result.print_step2", "Transfer the image to a convenience store multi-copy machine via USB memory or smartphone app")}</li>
          <li>{t("result.print_step3", "Print in L-size (100% scale, 'borderless' setting off)")}</li>
        </ol>
      </div>

      <div className="print-note">
        <p>
          {t("result.print_note")}
        </p>
      </div>

      <div className="button-container">
        <button onClick={onDownload} className="download-button">
          {t("buttons.download")}
        </button>
        <button onClick={onReset} className="reset-button">
          {t("buttons.reset")}
        </button>
      </div>
    </div>
  );
};

export default ResultStep;
